import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  UserCheck, Search, Clock, CheckCircle, XCircle, AlertCircle,
  FileText, Shield, Eye, Upload, MessageSquare, User, Calendar
} from 'lucide-react';

const VALIDATION_STEPS = [
  { id: 'identidade', label: 'Verificação de Identidade', icon: User },
  { id: 'rgpd', label: 'Consentimento RGPD', icon: Shield },
  { id: 'formacao', label: 'Formação Inicial', icon: FileText },
  { id: 'entrevista', label: 'Entrevista', icon: MessageSquare },
  { id: 'aprovacao_final', label: 'Aprovação Final', icon: CheckCircle }
];

const STATUS_COLORS = {
  pendente: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  em_analise: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  aprovado: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  rejeitado: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
};

const STATUS_LABELS = {
  pendente: 'Pendente',
  em_analise: 'Em Análise',
  aprovado: 'Aprovado',
  rejeitado: 'Rejeitado'
};

const ValidacaoVoluntariosPage = () => {
  const [voluntarios, setVoluntarios] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedVoluntario, setSelectedVoluntario] = useState(null);
  const [validationStatus, setValidationStatus] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isStepDialogOpen, setIsStepDialogOpen] = useState(false);
  const [isRGPDDialogOpen, setIsRGPDDialogOpen] = useState(false);
  const [stepForm, setStepForm] = useState({
    step_type: '',
    status: 'aprovado',
    notas: '',
    evidence_url: ''
  });
  const [rgpdForm, setRGPDForm] = useState({
    accepted: true,
    consent_type: 'geral',
    evidence_type: 'digital',
    evidence_description: '',
    consent_text: 'Autorizo o tratamento dos meus dados pessoais para efeitos de voluntariado.'
  });

  useEffect(() => {
    fetchVoluntarios();
    fetchStats();
  }, []);

  const fetchVoluntarios = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/voluntarios');
      setVoluntarios(response.data);
    } catch (error) {
      toast.error('Erro ao carregar voluntários');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/voluntarios/stats/validacao');
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const fetchValidationStatus = async (voluntarioId) => {
    try {
      const response = await apiClient.get(`/voluntarios/${voluntarioId}/validation-status`);
      setValidationStatus(response.data);
    } catch (error) {
      console.error('Erro ao carregar estado de validação:', error);
    }
  };

  const handleViewDetails = async (voluntario) => {
    setSelectedVoluntario(voluntario);
    await fetchValidationStatus(voluntario.id);
    setIsDetailOpen(true);
  };

  const handleAddStep = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/voluntarios/${selectedVoluntario.id}/validation-step`, stepForm);
      toast.success('Passo de validação adicionado');
      await fetchValidationStatus(selectedVoluntario.id);
      fetchVoluntarios();
      fetchStats();
      setIsStepDialogOpen(false);
      setStepForm({ step_type: '', status: 'aprovado', notas: '', evidence_url: '' });
    } catch (error) {
      toast.error('Erro ao adicionar passo de validação');
    }
  };

  const handleRecordRGPD = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/voluntarios/${selectedVoluntario.id}/rgpd-consent`, rgpdForm);
      toast.success('Consentimento RGPD registado');
      await fetchValidationStatus(selectedVoluntario.id);
      setIsRGPDDialogOpen(false);
    } catch (error) {
      toast.error('Erro ao registar consentimento RGPD');
    }
  };

  const handleApprove = async () => {
    try {
      await apiClient.put(`/voluntarios/${selectedVoluntario.id}/approve`, {
        notas: 'Voluntário aprovado após validação completa'
      });
      toast.success('Voluntário aprovado com sucesso');
      fetchVoluntarios();
      fetchStats();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error('Erro ao aprovar voluntário');
    }
  };

  const handleReject = async () => {
    const motivo = prompt('Motivo da rejeição:');
    if (!motivo) return;
    try {
      await apiClient.put(`/voluntarios/${selectedVoluntario.id}/reject`, {
        notas: motivo,
        motivo: motivo
      });
      toast.success('Voluntário rejeitado');
      fetchVoluntarios();
      fetchStats();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error('Erro ao rejeitar voluntário');
    }
  };

  const getVoluntarioStatus = (vol) => {
    return vol.validation_status || 'pendente';
  };

  const filteredVoluntarios = voluntarios.filter(v => {
    const matchSearch = v.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       v.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = !filterStatus || getVoluntarioStatus(v) === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6" data-testid="validacao-voluntarios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Validação de Voluntários</h1>
          <p className="text-slate-600 dark:text-slate-400">Workflow de validação e conformidade RGPD</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                <User className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Total</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Pendentes</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.pendentes || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Em Análise</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.em_analise || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Aprovados</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.aprovados || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Rejeitados</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.rejeitados || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-voluntarios-input"
                />
              </div>
            </div>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="em_analise">Em Análise</SelectItem>
                <SelectItem value="aprovado">Aprovado</SelectItem>
                <SelectItem value="rejeitado">Rejeitado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Voluntarios List */}
      <Card>
        <CardHeader>
          <CardTitle>Voluntários</CardTitle>
          <CardDescription>{filteredVoluntarios.length} voluntários encontrados</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin" />
            </div>
          ) : filteredVoluntarios.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <UserCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum voluntário encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredVoluntarios.map(vol => {
                const status = getVoluntarioStatus(vol);
                return (
                  <div
                    key={vol.id}
                    className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    data-testid={`voluntario-item-${vol.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#0066CC]/10 flex items-center justify-center">
                        <User className="w-6 h-6 text-[#0066CC]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900 dark:text-white">{vol.nome}</h3>
                          <Badge className={STATUS_COLORS[status]}>
                            {STATUS_LABELS[status]}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 dark:text-slate-400">
                          <span>{vol.email}</span>
                          {vol.telefone && <span>{vol.telefone}</span>}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {vol.competencias?.slice(0, 3).map(comp => (
                            <span key={comp} className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                              {comp}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleViewDetails(vol)}
                        data-testid={`view-voluntario-${vol.id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Validar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Validação - {selectedVoluntario?.nome}</DialogTitle>
            <DialogDescription>
              Workflow de validação e consentimento RGPD
            </DialogDescription>
          </DialogHeader>
          
          {validationStatus && (
            <Tabs defaultValue="workflow" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="workflow">Workflow</TabsTrigger>
                <TabsTrigger value="rgpd">RGPD</TabsTrigger>
                <TabsTrigger value="documentos">Documentos</TabsTrigger>
              </TabsList>
              
              <TabsContent value="workflow" className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <Badge className={STATUS_COLORS[validationStatus.validation_status || 'pendente']}>
                    {STATUS_LABELS[validationStatus.validation_status || 'pendente']}
                  </Badge>
                  <Button size="sm" onClick={() => setIsStepDialogOpen(true)}>
                    Adicionar Passo
                  </Button>
                </div>
                
                {/* Validation Steps Progress */}
                <div className="space-y-3">
                  {VALIDATION_STEPS.map((step, index) => {
                    const completed = validationStatus.validation_steps?.find(s => 
                      s.step_type === step.id && s.status === 'aprovado'
                    );
                    const rejected = validationStatus.validation_steps?.find(s => 
                      s.step_type === step.id && s.status === 'rejeitado'
                    );
                    const Icon = step.icon;
                    
                    return (
                      <div 
                        key={step.id} 
                        className={`flex items-center gap-3 p-3 rounded-lg border ${
                          completed ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
                          rejected ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' :
                          'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          completed ? 'bg-green-100 dark:bg-green-900' :
                          rejected ? 'bg-red-100 dark:bg-red-900' :
                          'bg-slate-200 dark:bg-slate-700'
                        }`}>
                          {completed ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : rejected ? (
                            <XCircle className="w-4 h-4 text-red-600" />
                          ) : (
                            <Icon className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{step.label}</p>
                          {(completed || rejected) && (
                            <p className="text-xs text-slate-500">
                              {(completed || rejected)?.performed_by_name} - {new Date((completed || rejected)?.performed_at).toLocaleDateString('pt-PT')}
                            </p>
                          )}
                        </div>
                        {completed && <Badge variant="outline" className="text-green-600">Concluído</Badge>}
                        {rejected && <Badge variant="outline" className="text-red-600">Rejeitado</Badge>}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
              
              <TabsContent value="rgpd" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Consentimentos RGPD</h4>
                  <Button size="sm" onClick={() => setIsRGPDDialogOpen(true)}>
                    <Shield className="w-4 h-4 mr-2" />
                    Registar Consentimento
                  </Button>
                </div>
                
                {Object.keys(validationStatus.rgpd_consent || {}).length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum consentimento registado</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(validationStatus.rgpd_consent || {}).map(([type, consent]) => (
                      <div key={type} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant={consent.accepted ? 'default' : 'destructive'}>
                            {consent.accepted ? 'Aceite' : 'Não Aceite'}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {new Date(consent.recorded_at).toLocaleDateString('pt-PT')}
                          </span>
                        </div>
                        <p className="text-sm font-medium">{type}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Tipo de evidência: {consent.evidence_type}
                        </p>
                        {consent.evidence_description && (
                          <p className="text-xs text-slate-500 mt-1">
                            {consent.evidence_description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="documentos" className="space-y-4">
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Funcionalidade de upload em desenvolvimento</p>
                </div>
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
              Fechar
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              <XCircle className="w-4 h-4 mr-2" />
              Rejeitar
            </Button>
            <Button onClick={handleApprove}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Aprovar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Step Dialog */}
      <Dialog open={isStepDialogOpen} onOpenChange={setIsStepDialogOpen}>
        <DialogContent>
          <form onSubmit={handleAddStep}>
            <DialogHeader>
              <DialogTitle>Adicionar Passo de Validação</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Tipo de Validação</Label>
                <Select value={stepForm.step_type} onValueChange={(v) => setStepForm({ ...stepForm, step_type: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {VALIDATION_STEPS.map(step => (
                      <SelectItem key={step.id} value={step.id}>{step.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={stepForm.status} onValueChange={(v) => setStepForm({ ...stepForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aprovado">Aprovado</SelectItem>
                    <SelectItem value="rejeitado">Rejeitado</SelectItem>
                    <SelectItem value="em_analise">Em Análise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  value={stepForm.notas}
                  onChange={(e) => setStepForm({ ...stepForm, notas: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsStepDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* RGPD Consent Dialog */}
      <Dialog open={isRGPDDialogOpen} onOpenChange={setIsRGPDDialogOpen}>
        <DialogContent>
          <form onSubmit={handleRecordRGPD}>
            <DialogHeader>
              <DialogTitle>Registar Consentimento RGPD</DialogTitle>
              <DialogDescription>
                Registe a evidência do consentimento RGPD do voluntário
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Tipo de Consentimento</Label>
                <Select value={rgpdForm.consent_type} onValueChange={(v) => setRGPDForm({ ...rgpdForm, consent_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="geral">Geral</SelectItem>
                    <SelectItem value="dados_pessoais">Dados Pessoais</SelectItem>
                    <SelectItem value="comunicacoes">Comunicações</SelectItem>
                    <SelectItem value="imagem">Imagem</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo de Evidência</Label>
                <Select value={rgpdForm.evidence_type} onValueChange={(v) => setRGPDForm({ ...rgpdForm, evidence_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="digital">Digital (checkbox/formulário)</SelectItem>
                    <SelectItem value="documento">Documento Assinado</SelectItem>
                    <SelectItem value="verbal">Verbal (testemunhado)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descrição da Evidência</Label>
                <Textarea
                  value={rgpdForm.evidence_description}
                  onChange={(e) => setRGPDForm({ ...rgpdForm, evidence_description: e.target.value })}
                  placeholder="Descreva como foi obtido o consentimento..."
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="accepted"
                  checked={rgpdForm.accepted}
                  onChange={(e) => setRGPDForm({ ...rgpdForm, accepted: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <Label htmlFor="accepted">Consentimento aceite</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsRGPDDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Registar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ValidacaoVoluntariosPage;
